import { Head, Link, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { StatusChip } from "@/components/ui/status-chip"
import { Switch } from "@/components/ui/switch"
import type { PageProps } from "@/types/inertia"

type FeatureRow = {
  key: string
  label: string
  description: string
  enabled: boolean
}

type PageRow = {
  key: string
  label: string
  path: string
  toggleable: boolean
  visible: boolean
  // Only set for condition/therapy pages, which use draft/published on their
  // own settings screens instead of the switch.
  draft: boolean | null
  settings_href: string | null
  hidden_section_count: number
  editor_href: string | null
}

type BrandGroup = {
  key: string
  name: string
  pages: PageRow[]
}

type Props = PageProps & {
  features: FeatureRow[]
  brands: BrandGroup[]
}

function PageRowItem({ page }: { page: PageRow }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-hairline bg-white px-4 py-3 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {page.editor_href ? (
            <Link href={page.editor_href} className="font-medium text-ink-display no-underline">
              {page.label}
            </Link>
          ) : (
            <span className="font-medium text-ink-display">{page.label}</span>
          )}
          <span className="text-xs text-ink-muted">{page.path}</span>
          {page.draft !== null && <StatusChip status={page.draft ? "draft" : "published"} />}
          {!page.toggleable && page.draft === null && (
            <span className="text-xs text-ink-muted">always on</span>
          )}
        </div>
        {page.hidden_section_count > 0 && page.editor_href && (
          <Link href={page.editor_href} className="mt-0.5 inline-block text-xs">
            {page.hidden_section_count} {page.hidden_section_count === 1 ? "section" : "sections"}{" "}
            hidden →
          </Link>
        )}
      </div>
      {page.toggleable ? (
        <Switch
          size="sm"
          checked={page.visible}
          onChange={(visible) =>
            router.patch(
              `/admin/website/pages/${page.key}/visibility`,
              { visible },
              { preserveScroll: true }
            )
          }
          aria-label={page.label}
        />
      ) : page.settings_href ? (
        <Link href={page.settings_href} className="text-xs">
          Page settings →
        </Link>
      ) : null}
    </div>
  )
}

export default function FeaturesIndex() {
  const { props } = usePage<Props>()
  const flash = props.flash

  return (
    <AdminShell>
      <Head title="Features">
        <meta
          name="description"
          content="Switch parts of the website on or off — features, pages and sections."
        />
        <meta property="og:title" content="Features" />
        <meta
          property="og:description"
          content="Switch parts of the website on or off — features, pages and sections."
        />
      </Head>

      <PageHeader
        title="Features"
        description="Switch parts of the website on or off. Green means it's live for visitors; red means it isn't there. Nothing is deleted — everything comes straight back when switched on."
      />

      {flash?.notice && <p className="mt-4 text-sm text-accent">{flash.notice}</p>}

      <h2 className="mt-8">Site-wide features</h2>
      <div className="mt-4 overflow-hidden rounded-lg border border-hairline">
        {props.features.map((feature) => (
          <div
            key={feature.key}
            className="flex flex-wrap items-center gap-4 border-b border-hairline bg-white px-4 py-4 last:border-b-0"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-ink-display">{feature.label}</span>
                <Badge tone={feature.enabled ? "accent" : "neutral"}>
                  {feature.enabled ? "On" : "Off"}
                </Badge>
              </div>
              <p className="mt-1 text-sm text-ink-muted">{feature.description}</p>
            </div>
            <Switch
              checked={feature.enabled}
              onChange={(enabled) =>
                router.patch(`/admin/features/${feature.key}`, { enabled }, { preserveScroll: true })
              }
              aria-label={feature.label}
            />
          </div>
        ))}
      </div>

      {props.brands.map((brand) => (
        <div key={brand.key} className="mt-8 max-w-3xl">
          <h2>{brand.name} pages</h2>
          <p className="mt-1 text-sm text-ink-muted">
            A page switched off gives visitors “page not found” and drops out of the menus and
            Google's sitemap — its content is kept. Condition and therapy pages publish from their
            own Page settings instead.
          </p>
          <div className="mt-4 overflow-hidden rounded-lg border border-hairline">
            {brand.pages.map((page) => (
              <PageRowItem key={page.key} page={page} />
            ))}
          </div>
        </div>
      ))}
    </AdminShell>
  )
}
